import { MenuList } from '@mui/material';
import React, { useEffect } from 'react';

import { PopperSubmenuContainer } from '../Components/PopperSubmenuContainer';
import { useMenuContext } from '../ContextState/useMenuContext';

import { DATA_TEST } from '../enums';
import { IMenuOption } from '../types';
import { itemIsLeafNode } from '../utils/utils';

import { useCombinedRefs } from '../utils/hooks/useCombinedRefs';
import { LevelOneMenuItem } from './LevelOneMenuItem';
import { MoreMenuItem } from './MoreMenuItem';

interface IMoreMenuTabProps {
  overflowMenuItems: IMenuOption[];
}

export const MoreMenuTab = React.forwardRef<HTMLLIElement, IMoreMenuTabProps>(({ overflowMenuItems }, ref) => {
  const { showMoreMenu, setShowMoreMenu, currentLevelOneItem, setCurrentLevelOneItem } = useMenuContext();

  const firstMoreMenuItemRef = React.useRef<HTMLLIElement>(null);

  const overflowItemSelected = overflowMenuItems?.some((item) => {
    return item.id === currentLevelOneItem?.id;
  });
  const [anchorEl, setAnchorEl] = React.useState<HTMLLIElement | null>(null);

  const menuItemRef = React.useRef<HTMLLIElement | null>(null);
  const combinedRef = useCombinedRefs(ref, menuItemRef);

  // Prevent the popper submenu from losing its anchor element if the menu is unexpectedly unmounted
  useEffect(() => {
    setAnchorEl(menuItemRef.current);
    return () => {
      setAnchorEl(null);
    };
  }, []);

  return (
    <>
      <LevelOneMenuItem
        ref={combinedRef}
        item={{
          id: 'more-menu-button',
          label: 'More',
          tooltipText: 'Show additional menu items'
        }}
        isSelected={showMoreMenu || overflowItemSelected}
        isActive={overflowMenuItems?.some((item) => item?.isActive)}
        ariaControlsId="more-menu-button"
        handleClick={() => {
          setShowMoreMenu((s) => !s);
          setCurrentLevelOneItem(null);
        }}
        firstLevelTwoItemRef={firstMoreMenuItemRef}
        dataTest={DATA_TEST.more_top_level_menu_item}
      />
      <PopperSubmenuContainer
        anchorEl={() => ({
          // Ensures that a warning does not appear in console in certain cases when menu item is removed from the DOM
          // Fixes: https://opengovinc.atlassian.net/browse/PSS-1446
          // Code based on: https://mui.com/material-ui/react-tooltip/#virtual-element
          getBoundingClientRect: () => {
            const rect = anchorEl?.getBoundingClientRect();
            return rect ? new DOMRect(rect.x, rect.y + rect.height, 0, 0) : new DOMRect();
          }
        })}
        open={showMoreMenu && anchorEl !== null}
        disablePortal={true}
        dataTest={DATA_TEST.popper_more_menu_container}
        placement="bottom-start"
        handleClose={() => {
          // The MUI ClickAwayListener callback resists testing efforts...
          // Omitting it from test coverage for now
          /* istanbul ignore next */
          setShowMoreMenu(false);
        }}
        positionOffset={{ x: 0, y: 3 }}
        styleOverrides={{ maxHeight: '80vh', overflow: 'hidden' }}
      >
        <MenuList
          data-test={DATA_TEST.more_menu_container}
          id="more-menu"
          aria-labelledby="more-menu-button"
          sx={{
            maxHeight: 'calc(80vh - 60px)',
            overflow: 'auto'
          }}
        >
          {overflowMenuItems?.map((item, index) => {
            const isLeafNode = itemIsLeafNode(item);
            const handleClick = () => {
              if (item.onClick) {
                item.onClick();
                setCurrentLevelOneItem(null);
                setShowMoreMenu(false);
                return;
              }
              if (item.url) {
                setCurrentLevelOneItem(null);
                setShowMoreMenu(false);
                return;
              }
              setCurrentLevelOneItem(item);
            };
            return (
              <MoreMenuItem
                key={item.id}
                item={item}
                index={index}
                handleClick={handleClick}
                firstMoreMenuItemRef={firstMoreMenuItemRef}
                anchorRef={menuItemRef}
                isLeafNode={isLeafNode}
              />
            );
          })}
        </MenuList>
      </PopperSubmenuContainer>
    </>
  );
});
